import { useState, useCallback, useRef, useEffect } from 'react';
import { isEmpty, mapValues, keyBy, groupBy, orderBy } from 'lodash';
import { QUERY_NAMES_V2 } from 'react/services/AudienceServiceHelper';
import {
  getSegmentInterestsDataByIds,
  getSegmentAdvancedInterestsDataByQueries,
} from 'react/services/AudienceInsightsService';
import * as MixpanelAudience from 'react/infra/mixpanel/MixpanelAudience';
import { ALL_INTERESTS } from '../constants';
import {
  throwForInvalidDataResponse,
  transformInterestsData,
  continueIfNoData,
  indexByIdAndMergeByView,
} from '../utils/audienceInsightsUtils';

/**
 * Hook for managing tested interests (add, delete, cache)
 */
const useTestedInterests = ({ segment, audienceChannel, targetRatioObj, context, notificator }) => {
  const [newlyAddedInterestsToTest, setNewlyAddedInterestsToTest] = useState({});

  // Refs for tested interests promises
  const testedInterestsByIdPromisesRef = useRef({});
  const readyTestedInterestsByIdPromisesRef = useRef({});

  // Track previous segment to detect changes
  const prevSegmentRef = useRef(segment);

  // Clear all highlighted interests cache when segment changes
  useEffect(() => {
    // Skip on initial mount
    if (prevSegmentRef.current === segment) return;
    prevSegmentRef.current = segment;

    // Clear tested interests promises cache
    testedInterestsByIdPromisesRef.current = {};
    readyTestedInterestsByIdPromisesRef.current = {};

    // Clear context storage (including categories)
    context.current.audience_app[audienceChannel].audience_interestsToTest = {};

    // Clear newly added state
    setNewlyAddedInterestsToTest({});
  }, [segment, audienceChannel, context]);

  // Initialize context storage for tested interests (like master)
  if (!context.current.audience_app[audienceChannel].audience_interestsToTest) {
    context.current.audience_app[audienceChannel].audience_interestsToTest = {};
  }

  /**
   * Get promise of tested interests by type
   * Only waits for READY promises to avoid blocking on pending restoration
   * Pending promises (testedInterestsByIdPromisesRef) are skipped - restoration will update view when done
   */
  const getPromiseOfTestedInterestsByType = useCallback((type) => {
    // Only use ready promises - don't block on pending (restoration) promises
    const promise = readyTestedInterestsByIdPromisesRef.current[type];
    if (!promise) {
      return Promise.resolve({ forChart: [], forTable: [] });
    }

    return promise.then((testedInterestsCache) => {
      const result = mapValues(testedInterestsCache, Object.values);
      return result;
    });
  }, []);

  /**
   * Test regular interests (non-boolean logic)
   */
  const testRegularInterests = useCallback(
    (type, interestsToTest, extraParams = {}) => {
      if (isEmpty(interestsToTest)) return Promise.resolve([]);

      const idToTextMap = mapValues(keyBy(interestsToTest, 'id'), 'text');
      return getSegmentInterestsDataByIds(
        segment,
        QUERY_NAMES_V2[type],
        audienceChannel,
        interestsToTest.map((r) => r.id),
        extraParams
      )
        .then(throwForInvalidDataResponse)
        .catch(continueIfNoData)
        .then(({ words = [] }) => words.map((i) => Object.assign(i, { phrase: idToTextMap[i.id] || i.id })));
    },
    [segment, audienceChannel]
  );

  /**
   * Test advanced interests (boolean logic)
   */
  const testAdvancedInterests = useCallback(
    (type, interestsToTest, extraParams = {}) => {
      if (isEmpty(interestsToTest)) return Promise.resolve([]);

      MixpanelAudience.trackTestingInterests(type, interestsToTest, true, audienceChannel);

      const idToTextMap = mapValues(keyBy(interestsToTest, 'id'), 'text');
      const advancedInterestsObjsForReq = interestsToTest.map((o) => ({
        id: o.id,
        required: (o.required || []).map((r) => r.id),
        included: (o.included || []).map((i) => i.id),
        excluded: (o.excluded || []).map((e) => e.id),
      }));

      return getSegmentAdvancedInterestsDataByQueries(
        segment,
        QUERY_NAMES_V2[type],
        audienceChannel,
        advancedInterestsObjsForReq,
        extraParams
      )
        .then(throwForInvalidDataResponse)
        .catch(continueIfNoData)
        .then(({ words: interests = [] }) => {
          const disabledTooltip = 'Refined interests are inapplicable for targeting.';
          return interests.map((i) => Object.assign(i, { phrase: idToTextMap[i.id], disabled: true, disabledTooltip }));
        });
    },
    [segment, audienceChannel]
  );

  /**
   * Get all tested interests by ID and update cache
   */
  const getAllTestedInterestsByIdByViewAndUpdateCache = useCallback(
    (
      searchResults,
      { type, topicId = ALL_INTERESTS, refInterestsPromise } // eslint-disable-line arrow-body-style
    ) =>
      refInterestsPromise.then((refInterestsByView) => {
        // Get from API only interests that do not already exist in top interests
        const existingTestedInterests = mapValues(refInterestsByView, (interests) =>
          interests.filter((i) => searchResults.some((r) => r.id === i.id))
        );
        const existingTestedInterestIds = existingTestedInterests.forTable.map((i) => i.id);
        const nonexistingInterestsToTest = searchResults.filter((r) => !existingTestedInterestIds.includes(r.id));

        // Calc min and max g values in current interests for score calculation in server
        const refInterestScoreValues = refInterestsByView.forTable.map((w) => w.g);
        const gInfoObj = { 'max-g': Math.max(...refInterestScoreValues), 'min-g': Math.min(...refInterestScoreValues) };
        const extraParams = { ...gInfoObj, ...targetRatioObj };

        // Separate advanced (boolean logic) from regular interests
        const grouped = groupBy(nonexistingInterestsToTest, (r) => r.type === 'programBL' || r.type === 'booleanLogic');
        const advancedInterestsToTest = grouped[true] || [];
        const regularInterestsToTest = grouped[false] || [];

        const testedRegularInterestsPromise = testRegularInterests(type, regularInterestsToTest, extraParams);
        const testedAdvancedInterestsPromise = testAdvancedInterests(type, advancedInterestsToTest, extraParams);

        return Promise.all([testedRegularInterestsPromise, testedAdvancedInterestsPromise]).then(
          ([testedRegularInterests, testedAdvancedInterests]) => {
            const allTestedInterests = testedRegularInterests.concat(testedAdvancedInterests);
            const newTestedInterestsByTopic = transformInterestsData(allTestedInterests, type, type === 'websites');
            const newTestedInterests = mapValues(newTestedInterestsByTopic, (interests) => interests[topicId]);

            // Combine new-found interests from API and top existing, and mark as highlighted for view
            const defaultTestedInterestProps = {
              highlighted: true,
              deletable: true,
              highlightMarkTooltip: 'Manually added',
            };
            const allTestedInterestsByView = mapValues(existingTestedInterests, (arr, view) =>
              (newTestedInterests[view] || []).concat(arr || [])
            );
            const result = mapValues(allTestedInterestsByView, (interests) =>
              interests.map((i) => Object.assign({}, i, defaultTestedInterestProps))
            );
            return result;
          }
        );
      }),
    [targetRatioObj, testRegularInterests, testAdvancedInterests]
  );

  /**
   * Notify for missing interests that couldn't be tested
   */
  const notifyForMissingInterestsToTest = useCallback(
    (testedInterestsByView, searchResults) => {
      const isAnyTested = testedInterestsByView.forTable && !isEmpty(testedInterestsByView.forTable);
      const missingInterestsToTest = isAnyTested
        ? searchResults.filter((r) => !testedInterestsByView.forTable.find((i) => i.id === r.id))
        : searchResults;

      if (missingInterestsToTest && missingInterestsToTest.length) {
        notificator.notify(`Sorry, insufficient data about ${missingInterestsToTest.map((r) => r.text).join(', ')}`);
      }

      return testedInterestsByView;
    },
    [notificator]
  );

  /**
   * Delete a tested interest
   */
  const deleteTestedInterest = useCallback(
    (id, interestType, setInterests, publishAllInterestsToView) => {
      // Handle categories separately - they use direct state updates, not cache
      if (interestType === 'categories') {
        // Remove from context
        const interestsToTest = context.current.audience_app[audienceChannel].audience_interestsToTest.categories;
        if (interestsToTest) {
          const index = interestsToTest.findIndex((r) => r.id === id);
          if (index !== -1) {
            interestsToTest.splice(index, 1);
          }
        }

        // Update interests state
        setInterests((prev) => {
          const item = prev?.find((i) => i.id === id);

          // If item was added from search (not in original top 200), remove it entirely
          if (item?.wasAddedFromSearch) {
            return prev?.filter((i) => i.id !== id);
          }

          // Otherwise, just unhighlight and re-sort
          const updated = prev?.map((i) =>
            i.id === id ? { ...i, highlighted: false, deletable: false, highlightMarkTooltip: undefined } : i
          );

          // Re-sort: highlighted items at top (desc), then by uniqueness-index desc
          return orderBy(updated, ['highlighted', 'uniqueness-index'], ['desc', 'desc']);
        });
        return;
      }

      // Remove from context immediately (always do this regardless of cache state)
      const interestsToTest = context.current.audience_app[audienceChannel].audience_interestsToTest[interestType];
      if (interestsToTest) {
        const contextIndex = interestsToTest.findIndex((r) => r.id === id);
        if (contextIndex !== -1) {
          interestsToTest.splice(contextIndex, 1);
        }
      }

      // Use READY ref only (don't wait for pending restoration)
      const deleteInterestPromise = readyTestedInterestsByIdPromisesRef.current[interestType]
        ? readyTestedInterestsByIdPromisesRef.current[interestType].then((testedInterestsById) => {
            // Remove from tested interests pools
            if (testedInterestsById.forChart) {
              const index = testedInterestsById.forChart.findIndex((i) => i.id === id);
              if (index !== -1) {
                testedInterestsById.forChart.splice(index, 1);
              }
            }
            if (testedInterestsById.forTable) {
              const index = testedInterestsById.forTable.findIndex((i) => i.id === id);
              if (index !== -1) {
                testedInterestsById.forTable.splice(index, 1);
              }
            }
            return testedInterestsById;
          })
        : Promise.resolve({});

      // Chain the promise and update BOTH refs
      testedInterestsByIdPromisesRef.current[interestType] = deleteInterestPromise;
      readyTestedInterestsByIdPromisesRef.current[interestType] = deleteInterestPromise;

      // Refresh view by reloading all interests
      deleteInterestPromise.then(() => {
        publishAllInterestsToView();
      });
    },
    [context, audienceChannel]
  );

  /**
   * Save tested interests to context (like master - uses context only, no sessionStorage)
   */
  const saveTestedInterestsToContext = useCallback(
    (type, searchResults) => {
      const pastInterestsToTest = context.current.audience_app[audienceChannel].audience_interestsToTest[type] || [];
      const newInterests = searchResults.filter(
        (newItem) => !pastInterestsToTest.some((oldItem) => oldItem.id === newItem.id)
      );
      context.current.audience_app[audienceChannel].audience_interestsToTest[type] = [
        ...pastInterestsToTest,
        ...newInterests,
      ];
    },
    [context, audienceChannel]
  );

  /**
   * Process new tested interests and update cache
   */
  const processNewTestedInterests = useCallback(
    (type, searchResults, getPromiseOfInterestsByTypeAndTopic, publishCallback) => {
      // Update ready promise reference
      readyTestedInterestsByIdPromisesRef.current[type] = testedInterestsByIdPromisesRef.current[type];

      // Fetch tested interests data
      const testedInterestsPromise = getAllTestedInterestsByIdByViewAndUpdateCache(searchResults, {
        type,
        topicId: ALL_INTERESTS,
        refInterestsPromise: getPromiseOfInterestsByTypeAndTopic(type, ALL_INTERESTS),
      });

      // Update promises chain - merge with previous READY tested interests only
      // Don't wait for pending restoration - use readyRef instead of testedRef
      const newTestedInterestsPromise = Promise.all([
        testedInterestsPromise,
        readyTestedInterestsByIdPromisesRef.current[type] || Promise.resolve({}),
      ]).then(([testedInterestsByView, lastTestedInterestsByIdByView]) =>
        indexByIdAndMergeByView(testedInterestsByView, lastTestedInterestsByIdByView)
      );

      // Update the refs immediately with the promise
      testedInterestsByIdPromisesRef.current[type] = newTestedInterestsPromise;

      // Set loading promise and refresh view
      return Promise.all([testedInterestsPromise, newTestedInterestsPromise])
        .then(([testedInterestsByView]) => {
          // Update ready ref after resolution
          readyTestedInterestsByIdPromisesRef.current[type] = newTestedInterestsPromise;
          return notifyForMissingInterestsToTest(testedInterestsByView, searchResults);
        })
        .then(() => publishCallback())
        .then(() => {
          // Clear input bar for this type
          setNewlyAddedInterestsToTest((prev) => ({ ...prev, [type]: [] }));
        });
    },
    [getAllTestedInterestsByIdByViewAndUpdateCache, notifyForMissingInterestsToTest]
  );

  return {
    newlyAddedInterestsToTest,
    setNewlyAddedInterestsToTest,
    getPromiseOfTestedInterestsByType,
    getAllTestedInterestsByIdByViewAndUpdateCache,
    deleteTestedInterest,
    saveTestedInterestsToContext,
    processNewTestedInterests,
    testedInterestsByIdPromisesRef,
    readyTestedInterestsByIdPromisesRef,
  };
};

export default useTestedInterests;
